import React from 'react';
import { Typography } from 'antd';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getEntityOrganisationPath } from 'utils/get-entity-organisation-path';
import { ProviderIcon } from 'components/Custom/ProviderIcon';
import {
  Entity,
  GroupedConnection,
  Organisation,
} from 'store/organisation/models';

const { Text } = Typography;

const EntityListContainer = styled.div`
  border: 1px solid var(--color-border, #dbeae3);
  border-radius: 5px;
  background: #fafafa;
  padding: 8px;
`;

const ReadOnlyListScroll = styled.div`
  max-height: min(400px, calc(100vh - 280px));
  overflow-x: hidden;
  overflow-y: auto;
`;

const ReadOnlyEntityRow = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  padding: 8px 10px;
  border-radius: 5px;
  margin-bottom: 4px;
  background: #f5f7f6;
  border: 1px solid #e8efec;
  cursor: default;

  &:last-child {
    margin-bottom: 0;
  }
`;

const EntityInfo = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  margin-left: 0;
  gap: 10px;
  overflow: hidden;
`;

const EntityDetails = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const EntityNameBlock = styled.div`
  min-width: 0;
  width: 100%;
  max-width: min(220px, 100%);
  overflow: hidden;
`;

export type GroupConnectedMember = {
  id: string;
  name: string;
  vatNumber?: string;
  providerName?: string;
};

const EntityLogoLink = styled(Link)`
  display: inline-flex;
  flex-shrink: 0;
  line-height: 0;
  border-radius: 2px;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.85;
  }
`;

export const GroupConnectedEntitiesList: React.FC<{
  members: GroupConnectedMember[];
  showHint?: boolean;
}> = ({ members, showHint = true }) => {
  const { t } = useTranslation();
  const vatOrPlaceholder = (vat?: string) =>
    vat && String(vat).trim() ? String(vat).trim() : '—';

  return (
    <div>
      {showHint && (
        <Text
          style={{
            display: 'block',
            fontSize: 13,
            color: 'var(--color-text-muted, #5a6a6a)',
            marginBottom: 12,
          }}
        >
          {t('group.connected-entities-readonly-hint')}
        </Text>
      )}
      <EntityListContainer style={{ cursor: 'default' }}>
        <ReadOnlyListScroll>
          {members.length === 0 ? (
            <Text
              style={{
                color: 'var(--color-text-muted, #5a6a6a)',
                padding: '20px',
                display: 'block',
                textAlign: 'center',
              }}
            >
              {t('group.no-entities')}
            </Text>
          ) : (
            members.map((m) => {
              const to = getEntityOrganisationPath(m.id, m.providerName);
              const vatText = vatOrPlaceholder(m.vatNumber);
              return (
                <ReadOnlyEntityRow key={m.id}>
                  <EntityInfo>
                    <EntityLogoLink
                      to={to}
                      title={t('organisation.view-entity')}
                      aria-label={t('organisation.view-entity')}
                    >
                      {m.providerName ? (
                        <ProviderIcon
                          providerName={m.providerName}
                          alt={m.providerName}
                          width={20}
                          style={{ display: 'block' }}
                        />
                      ) : (
                        <span
                          style={{
                            display: 'inline-flex',
                            width: 20,
                            height: 20,
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 9,
                            fontWeight: 600,
                            backgroundColor: '#e8e8e8',
                            color: '#666',
                            borderRadius: 2,
                          }}
                        >
                          {(m.providerName || m.name || '?')
                            .charAt(0)
                            .toUpperCase()}
                        </span>
                      )}
                    </EntityLogoLink>
                    <EntityDetails>
                      <EntityNameBlock>
                        <Text
                          ellipsis={{ tooltip: m.name }}
                          style={{
                            fontSize: '13px',
                            display: 'block',
                            width: '100%',
                            fontWeight: 500,
                          }}
                        >
                          {m.name}
                        </Text>
                      </EntityNameBlock>
                      <Text
                        ellipsis={{ tooltip: vatText !== '—' ? vatText : undefined }}
                        style={{
                          fontSize: '11px',
                          color: 'var(--color-text-muted, #5a6a6a)',
                          lineHeight: '14px',
                          display: 'block',
                          maxWidth: '100%',
                        }}
                      >
                        {vatText}
                      </Text>
                    </EntityDetails>
                  </EntityInfo>
                </ReadOnlyEntityRow>
              );
            })
          )}
        </ReadOnlyListScroll>
      </EntityListContainer>
    </div>
  );
};

type Translate = (key: string) => string;

/** Map entity list (entities page) to read-only group member rows. */
export function buildMembersFromEntityList(
  memberIds: string[],
  allEntities: Entity[],
  t: Translate
): GroupConnectedMember[] {
  return memberIds.map((id) => {
    const entity = allEntities.find((e) => e.organisationId === id);
    return {
      id,
      name: entity
        ? entity.organisation
        : `${t('group.entity-not-available')} (${id})`,
      vatNumber: entity?.vatNumber,
      providerName: entity?.providerName,
    };
  });
}

export function buildMembersFromGroupedConnections(
  groupedConnections: GroupedConnection[] | undefined,
  allOrganisations: Organisation[] | undefined
): GroupConnectedMember[] {
  if (!groupedConnections?.length) return [];
  return groupedConnections.map((gc) => {
    const org = allOrganisations?.find((o) => o.uuid === gc.uuid);
    return {
      id: gc.uuid,
      name: org?.friendlyName || gc.name || gc.legalName || gc.uuid,
      vatNumber: (org?.taxNumber || gc.taxNumber) || undefined,
      providerName: gc.providerName,
    };
  });
}
